import React from 'react';
import { Spin, Tabs, Typography, Avatar, Card, Divider, Tag, Statistic } from 'antd';
import { Video, ISentiment } from '@/interfaces/video';
import { 
	FacebookOutlined,
	LinkedinOutlined,
	TwitterOutlined,
	YoutubeOutlined,
	PushpinFilled,
	InstagramOutlined,
	ReadOutlined,
	TikTokOutlined,
} from '@ant-design/icons';
import styles from './styles.module.css';
import Pie from '@/components/charts/pie';
import ChartSentiment from '@/components/charts/sentiment';
import Highlighter from 'react-highlight-words';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';

const { Text, Paragraph } = Typography;

interface IProps {
	video: Video;
	openSentiment: string;
	searchTerm?: string;
}

const DeepInfo: React.FC<IProps> = ({ video, openSentiment, searchTerm = '' }) => {
	const [sentiment, setSentiment] = React.useState<ISentiment[]>([]);
	const [showSearch, setShowSearch] = React.useState(localStorage.getItem("transcript-search") === "1" ? true : false);
	const [showBrands, setShowBrands] = React.useState(localStorage.getItem("transcript-brands") === "1" ? true : false);
	const [showProducts, setShowProducts] = React.useState(localStorage.getItem("transcript-products") === "1" ? true : false);
	const [highLight, setHighlight] = React.useState<string[]>([]);

	React.useEffect(() => {
		if (openSentiment !== video._id) {
			return;
		}

		// Process sentiment data from video content
		const sentimentData = video.content
			.map((segment, i) => ({
				entry: i,
				negative: -Math.abs(segment.sentiment.neg) * 100,
				negativeColor: '#ff4d50bf',
				neutral: segment.sentiment.neu * 100,
				positive: Math.abs(segment.sentiment.pos) * 100,
				positiveColor: '#73d13ddd',
				text: segment.text,
			} as ISentiment))
			.filter((v: ISentiment) => v.neutral !== 100);

		setSentiment(sentimentData);
	}, [openSentiment, video._id, video.content]);

	// Handle highlighting controls
	const setTag = React.useCallback(
		(tag: string) => {
			switch (tag) {
				case "search":
					setShowSearch(!showSearch);
					localStorage.setItem("transcript-search", !showSearch ? "1" : "0");
					return;
				case "brands":
					setShowBrands(!showBrands);
					localStorage.setItem("transcript-brands", !showBrands ? "1" : "0");
					return;
				case "products":
					setShowProducts(!showProducts);
					localStorage.setItem("transcript-products", !showProducts ? "1" : "0");
					return;
				default:
					break;
			}
		},
		[showSearch, showBrands, showProducts]
	);

	// Update highlight array when controls change
	React.useEffect(() => {
		const arr = [];
		if (showSearch && searchTerm) {
			arr.push(searchTerm);
		}
		if (showBrands && video.metaData.brandsMentioned) {
			arr.push(...video.metaData.brandsMentioned.map((v) => ` ${v} `));
		}
		if (showProducts && video.metaData.productsMentioned) {
			arr.push(...video.metaData.productsMentioned.map((v) => ` ${v} `));
		}
		setHighlight(arr);
	}, [showSearch, showBrands, showProducts, searchTerm, video.metaData.brandsMentioned, video.metaData.productsMentioned]);

	if (openSentiment !== video._id) {
		return <></>;
	}

	const tabItems = [
		{
			key: '1',
			label: 'Video Summary',
			children: (
				<div style={{ 
					position: 'relative',
					height: '446px',
					overflowX: 'hidden',
					overflowY: 'auto',
					padding: '20px 0'
				}}>
					<div style={{ padding: '0 20px' }}>
						<Typography.Title level={4}>Video Summary</Typography.Title>
						<Paragraph>{video.metaData.summary}</Paragraph>
					</div>
				</div>
			),
		},
		{
			key: '2',
			label: 'Profile',
			children: (
				<div style={{ 
					position: 'relative',
					height: '446px',
					overflowX: 'hidden',
					overflowY: 'auto',
					padding: '20px 0'
				}}>
					{/* Profile Header with Avatar and Name */}
					<div style={{ display: 'flex', marginBottom: '20px' }}>
						<div>
							<Avatar
								src={video.creatorData?.[0]?.platforms?.Youtube?.profileImageURL ? 
									 prepareImageSrc(video.creatorData[0].platforms.Youtube.profileImageURL) :
									 video.creatorData?.[0]?.platforms?.Instagram?.profileImageURL ?
									 prepareImageSrc(video.creatorData[0].platforms.Instagram.profileImageURL) :
									 video.creatorData?.[0]?.platforms?.Tiktok?.profileImageURL ?
									 prepareImageSrc(video.creatorData[0].platforms.Tiktok.profileImageURL) : undefined}
								size={{ xs: 24, sm: 32, md: 40, lg: 64, xl: 80, xxl: 100 }}
								style={{ marginRight: '20px' }}
							/>
						</div>
						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
							<b>
								<a 
									href={video.metaData.creatorUrl || ''} 
									target="_blank" 
									style={{ color: 'black', textDecoration: 'none' }}
									onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
									onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
								>
									{video.metaData.creatorName}
								</a>
							</b>
						</div>
					</div>

					{/* Description Section */}
					<div>
						<Divider orientationMargin="0" orientation="left">
							Description
						</Divider>
						<div style={{ whiteSpace: 'break-spaces' }}>
							{video.creatorData?.[0]?.platforms?.Youtube?.description || 
							 video.creatorData?.[0]?.platforms?.Instagram?.description || 
							 video.creatorData?.[0]?.platforms?.Tiktok?.description}
						</div>
					</div>

					{/* Links Section */}
					<div>
						<Divider orientationMargin="0" orientation="left">
							Links
						</Divider>
						<div style={{ margin: '10px 0' }}>
							{video.creatorData?.[0]?.platforms?.Youtube?.url && (
								<Tag icon={<YoutubeOutlined />} color="#cd201f">
									<a target="_blank" href={video.creatorData[0].platforms.Youtube.url} style={{ color: 'white' }}>
										YouTube
									</a>
								</Tag>
							)}
							{video.creatorData?.[0]?.platforms?.Instagram?.url && (
								<Tag icon={<InstagramOutlined />} color="#F77737">
									<a target="_blank" href={video.creatorData[0].platforms.Instagram.url} style={{ color: 'white' }}>
										Instagram
									</a>
								</Tag>
							)}
							{video.creatorData?.[0]?.platforms?.Tiktok?.url && (
								<Tag icon={<TikTokOutlined />} color="#000000">
									<a target="_blank" href={video.creatorData[0].platforms.Tiktok.url} style={{ color: 'white' }}>
										TikTok
									</a>
								</Tag>
							)}
						</div>
					</div>

					{/* Socials Section with Platform Statistics */}
					<div style={{ marginBottom: '90px' }}>
						<Divider orientationMargin="0" orientation="left">
							Socials
						</Divider>
						<div 
							className={styles['profile-socials']}
							style={{ 
								display: 'grid',
								gridTemplateColumns: 'repeat(3, 1fr)',
								columnGap: '20px',
								rowGap: '20px'
							}}
						>
							{/* YouTube Stats */}
							{video.creatorData?.[0]?.platforms?.Youtube && (
								<Card size="small" title="YouTube" variant="outlined">
									<div style={{ 
										display: 'grid',
										gridTemplateColumns: 'repeat(2, 1fr)',
										columnGap: '20px',
										marginBottom: '10px'
									}}>
										<Statistic title="Subscribers" value={video.creatorData[0].platforms.Youtube.connections?.subscribers || "N/A"} />
									</div>
									<div style={{ 
										display: 'grid',
										gridTemplateColumns: 'repeat(2, 1fr)',
										columnGap: '20px',
										marginBottom: '10px'
									}}>
										<Statistic title="Videos" value={video.creatorData[0].platforms.Youtube.posts?.videos || "N/A"} />
										<Statistic title="Views" value={video.creatorData[0].platforms.Youtube.stats?.views || "N/A"} />
									</div>
									<div style={{ 
										display: 'grid',
										gridTemplateColumns: 'repeat(2, 1fr)',
										columnGap: '20px',
										marginBottom: '10px'
									}}>
										<Statistic title="Likes" value={video.creatorData[0].platforms.Youtube.stats?.likes || "N/A"} />
										<Statistic title="Comments" value={video.creatorData[0].platforms.Youtube.stats?.comments || "N/A"} />
									</div>
								</Card>
							)}

							{/* Instagram Stats */}
							{video.creatorData?.[0]?.platforms?.Instagram && (
								<Card size="small" title="Instagram" variant="outlined">
									<div style={{ 
										display: 'grid',
										gridTemplateColumns: 'repeat(2, 1fr)',
										columnGap: '20px',
										marginBottom: '10px'
									}}>
										<Statistic title="Followers" value={video.creatorData[0].platforms.Instagram.connections?.subscribers || "N/A"} />
									</div>
									<div style={{ 
										display: 'grid',
										gridTemplateColumns: 'repeat(2, 1fr)',
										columnGap: '20px',
										marginBottom: '10px'
									}}>
										<Statistic title="Posts" value={video.creatorData[0].platforms.Instagram.posts?.videos || "N/A"} />
										<Statistic title="Views" value={video.creatorData[0].platforms.Instagram.stats?.views || "N/A"} />
									</div>
									<div style={{ 
										display: 'grid',
										gridTemplateColumns: 'repeat(2, 1fr)',
										columnGap: '20px',
										marginBottom: '10px'
									}}>
										<Statistic title="Likes" value={video.creatorData[0].platforms.Instagram.stats?.likes || "N/A"} />
										<Statistic title="Comments" value={video.creatorData[0].platforms.Instagram.stats?.comments || "N/A"} />
									</div>
								</Card>
							)}

							{/* TikTok Stats */}
							{video.creatorData?.[0]?.platforms?.Tiktok && (
								<Card size="small" title="TikTok" variant="outlined">
									<div style={{ 
										display: 'grid',
										gridTemplateColumns: 'repeat(2, 1fr)',
										columnGap: '20px',
										marginBottom: '10px'
									}}>
										<Statistic title="Followers" value={video.creatorData[0].platforms.Tiktok.connections?.subscribers || "N/A"} />
									</div>
									<div style={{ 
										display: 'grid',
										gridTemplateColumns: 'repeat(2, 1fr)',
										columnGap: '20px',
										marginBottom: '10px'
									}}>
										<Statistic title="Videos" value={video.creatorData[0].platforms.Tiktok.posts?.videos || "N/A"} />
										<Statistic title="Views" value={video.creatorData[0].platforms.Tiktok.stats?.views || "N/A"} />
									</div>
									<div style={{ 
										display: 'grid',
										gridTemplateColumns: 'repeat(2, 1fr)',
										columnGap: '20px',
										marginBottom: '10px'
									}}>
										<Statistic title="Likes" value={video.creatorData[0].platforms.Tiktok.stats?.likes || "N/A"} />
										<Statistic title="Comments" value={video.creatorData[0].platforms.Tiktok.stats?.comments || "N/A"} />
									</div>
								</Card>
							)}
						</div>
					</div>
				</div>
			),
		},
		{
			key: '3',
			label: 'Content Analysis',
			children: (
				<div style={{ 
					position: 'relative',
					height: '446px',
					overflowX: 'hidden',
					overflowY: 'auto',
					padding: '20px 0'
				}}>
					<div style={{ display: 'flex', flexDirection: 'row', gap: '20px', marginBottom: '90px' }}>
						{/* Left Column */}
						<div style={{ flex: 50 }}>
							<div style={{ display: 'flex', flexDirection: 'column' }}>
								{/* Sentiment Chart */}
								<div>
									<Divider orientationMargin="0" orientation="left">
										Sentiment
									</Divider>
									<div style={{ display: 'flex', justifyContent: 'center' }}>
										<div style={{ width: '200px', height: '200px' }}>
											<Pie 
												data={[
													{
														id: 'positive',
														label: 'positive',
														value: Math.round((video.metaData.SentimentSummary?.pos || 0) * 100),
														color: '#61cdbb',
													},
													{
														id: 'neutral',
														label: 'neutral',
														value: Math.round((video.metaData.SentimentSummary?.neu || 0) * 100),
														color: '#4096ffbe',
													},
													{
														id: 'negative',
														label: 'negative',
														value: Math.round((video.metaData.SentimentSummary?.neg || 0) * 100),
														color: '#f47560',
													},
												]}
											/>
										</div>
									</div>
								</div>

								{/* Comprehension */}
								<div>
									<Divider orientationMargin="0" orientation="left">
										Comprehension
									</Divider>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<div style={{ marginRight: '10px', color: '#1677ff', fontSize: 18 }}>
											<ReadOutlined />
										</div>
										{video.metaData.comprehension.toFixed(2)} - {
											video.metaData.comprehension < 6
												? 'basic'
												: video.metaData.comprehension < 13
												? 'average'
												: 'skilled'
										}
									</div>
								</div>

								{/* Links */}
								<div>
									<Divider orientationMargin="0" orientation="left">
										Links
									</Divider>
									{video.metaData.links && video.metaData.links.length > 0 ? (
										video.metaData.links.map((link, index) => (
											<div key={index}>
												<a style={{ textDecoration: 'underline' }} href={link} target="_blank" rel="noopener noreferrer">
													{link}
												</a>
											</div>
										))
									) : (
										<span>-</span>
									)}
								</div>
							</div>
						</div>

						{/* Right Column */}
						<div style={{ flex: 50 }}>
							{/* Brands Mentioned */}
							<div>
								<Divider orientationMargin="0" orientation="left">
									Brands mentioned
								</Divider>
								{video.metaData.brandsMentioned?.length > 0 ? (
									video.metaData.brandsMentioned.map((brand) => (
										<Tag key={brand} color="blue" style={{ cursor: 'pointer', marginBottom: '4px' }}>
											{brand}
										</Tag>
									))
								) : (
									<span>-</span>
								)}
							</div>

							{/* Entities */}
							<div>
								<Divider orientationMargin="0" orientation="left">
									Entities
								</Divider>
								{video.metaData.entities && video.metaData.entities.length > 0 ? (
									video.metaData.entities.map((entity) => (
										<Tag key={entity} style={{ marginBottom: '4px' }}>
											{entity}
										</Tag>
									))
								) : (
									<span>-</span>
								)}
							</div>

							{/* Hashtags */}
							<div>
								<Divider orientationMargin="0" orientation="left">
									Hashtags
								</Divider>
								{video.metaData.hashtags?.length > 0 ? (
									video.metaData.hashtags.map((hashtag) => (
										<Tag key={hashtag} color="orange" style={{ marginBottom: '4px' }}>
											{hashtag}
										</Tag>
									))
								) : (
									<span>-</span>
								)}
							</div>

							{/* Keywords */}
							<div>
								<Divider orientationMargin="0" orientation="left">
									Keywords
								</Divider>
								{video.metaData.keyWords && video.metaData.keyWords.length > 0 ? (
									video.metaData.keyWords.map((keyword) => (
										<Tag key={keyword} style={{ marginBottom: '4px' }}>
											{keyword}
										</Tag>
									))
								) : (
									<span>-</span>
								)}
							</div>
						</div>
					</div>
				</div>
			),
		},
		{
			key: '4',
			label: 'Sentiment',
			children: (
				<div style={{ 
					position: 'relative',
					height: '446px',
					overflowX: 'hidden',
					overflowY: 'auto',
					padding: '20px 0'
				}}>
					<div style={{ padding: '0 20px', marginBottom: '90px' }}>
						<ChartSentiment 
							id={video._id} 
							meta={video.metaData} 
							data={sentiment} 
						/>
					</div>
				</div>
			),
		},
		{
			key: '5',
			label: 'Context',
			children: (
				<div style={{ 
					position: 'relative',
					height: '446px',
					overflowX: 'hidden',
					overflowY: 'auto',
					padding: '20px 0'
				}}>
					<div style={{ padding: '0 20px', marginBottom: '90px' }}>
						{video.content?.map((segment, index) => {
							// Determine sentiment color
							const getSentimentColor = (sentiment: any) => {
								if (sentiment.pos > sentiment.neg && sentiment.pos > 0.1) return 'green';
								if (sentiment.neg > sentiment.pos && sentiment.neg > 0.1) return 'red';
								if (sentiment.pos > 0.1) return 'green';
								if (sentiment.neg > 0.1) return 'red';
								return 'blue';
							};

							// Format time to MM:SS
							const formatTime = (seconds: number) => {
								const mins = Math.floor(seconds / 60);
								const secs = Math.floor(seconds % 60);
								return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
							};

							return (
								<div key={segment.start || index}>
									<div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
										<Tag
											color={getSentimentColor(segment.sentiment)}
											style={{ 
												cursor: 'pointer', 
												marginRight: '10px',
												minWidth: '50px',
												textAlign: 'center'
											}}
											onClick={() => {
												window.open(`${video.metaData.url}&t=${Math.round(segment.start - 2)}s`);
											}}
										>
											{formatTime(Math.round(segment.start))}
										</Tag>
										<div
											style={{ 
												flex: 1, 
												cursor: 'pointer',
												lineHeight: '1.4'
											}}
											onClick={() => {
												window.open(`${video.metaData.url}&t=${Math.round(segment.start - 2)}s`);
											}}
										>
											<Highlighter
												highlightStyle={{ backgroundColor: "#ffec3d" }}
												searchWords={searchTerm ? [searchTerm] : []}
												autoEscape={true}
												textToHighlight={segment.text?.trim()}
											/>
										</div>
									</div>
									<Divider style={{ margin: "10px 0" }} />
								</div>
							);
						})}
					</div>
				</div>
			),
		},
		{
			key: '6',
			label: 'Transcript',
			children: (
				<div style={{ 
					position: 'relative',
					height: '446px',
					overflowX: 'hidden',
					overflowY: 'auto',
					padding: '20px 0'
				}}>
					<div style={{ padding: '0 20px', marginBottom: '90px' }}>
						{/* Highlighting Controls */}
						<div style={{ marginBottom: '20px' }}>
							<span style={{ marginRight: '10px' }}>Show: </span>
							<Tag 
								style={{ cursor: 'pointer', marginRight: '8px' }} 
								color={showSearch ? "blue" : ""} 
								onClick={() => setTag("search")}
							>
								search
							</Tag>
							<Tag 
								style={{ cursor: 'pointer', marginRight: '8px' }} 
								color={showBrands ? "blue" : ""} 
								onClick={() => setTag("brands")}
							>
								brands
							</Tag>
							<Tag 
								style={{ cursor: 'pointer', marginRight: '8px' }} 
								color={showProducts ? "blue" : ""} 
								onClick={() => setTag("products")}
							>
								products
							</Tag>
						</div>

						{/* Transcript Content */}
						<div style={{ maxHeight: '350px', overflowY: 'auto', marginLeft: '-20px', marginRight: '-20px', padding: '0 20px' }}>
							<Highlighter
								highlightStyle={{ backgroundColor: "#ffec3d" }}
								highlightClassName="YourHighlightClass"
								searchWords={highLight}
								autoEscape={true}
								textToHighlight={video.metaData.searchTranscription || video.metaData.text}
							/>
						</div>
					</div>
				</div>
			),
		},
	];

	return (
		<Spin spinning={false}>
			<Tabs
				defaultActiveKey="1"
				style={{
					padding: '10px 0px 20px 20px',
					height: '500px',
					overflow: 'hidden',
				}}
				destroyOnHidden
				items={tabItems}
			/>
		</Spin>
	);
};

export default DeepInfo;
